import {IncomingInvoiceId} from "../../models/accounting/IncomingInvoice";
import {ErrorAction, ReduxAction} from "../../models/common/ActionTypes";
import {Identifier} from "../../models/common/Identifier";
import {Observable, of} from "rxjs";
import {ofType, StateObservable} from "redux-observable";
import {AppState} from "../../reducers/RootReducer";
import {catchError, mergeMap, switchMap} from "rxjs/operators";
import {ajax} from "rxjs/ajax";
import {APP_CONFIG} from "../../config/GeneralConfig";
import {RouteMethods} from "../../config/RouteConfig";
import {eventNotificationError} from "../common/EventNotificationActions";
import {
    IncomingInvoicePayment,
    IncomingInvoicePaymentIdProp,
    IncomingInvoicePaymentModule
} from "../../models/accounting/IncomingInvoicePayment";
import {fetchIncomingInvoiceById} from "./IncomingInvoiceActions";
import {Nullable} from "../../models/common/Nullable";


/** backend api path */
function apiPath( id: Nullable< number > = null ): string {
    const uri = 'invoices/incoming/'
    if( id === null ) {
        return APP_CONFIG.baseWeb + uri;
    } else {
        return APP_CONFIG.baseWeb + uri  + id;
    }
}

/**
 *  insert invoice payment
 */
export interface InsertIncomingInvoicePaymentAction extends IncomingInvoiceId, ReduxAction< typeof INSERT_INCOMING_INVOICE_PAYMENT > {
    incomingInvoicePayment: IncomingInvoicePayment
}
export interface InsertIncomingInvoicePaymentSuccessAction extends ReduxAction<typeof INSERT_INCOMING_INVOICE_PAYMENT_SUCCESS > {
}
export interface InsertIncomingInvoicePaymentErrorAction extends ErrorAction< typeof INSERT_INCOMING_INVOICE_PAYMENT_ERROR >{}

export type InsertIncomingInvoicePaymentActionTypes = InsertIncomingInvoicePaymentAction
    | InsertIncomingInvoicePaymentErrorAction
    | InsertIncomingInvoicePaymentSuccessAction

/**
 * redux action constants
 */
export const INSERT_INCOMING_INVOICE_PAYMENT = "INSERT_INCOMING_INVOICE_PAYMENTS";
export const INSERT_INCOMING_INVOICE_PAYMENT_SUCCESS = "INSERT_INCOMING_INVOICE_PAYMENTS_SUCCESS";
export const INSERT_INCOMING_INVOICE_PAYMENT_ERROR = "INSERT_INCOMING_INVOICE_PAYMENTS_ERROR";

/**
 * redux action functions
 */
export function insertIncomingInvoicePayment(
    incomingInvoiceId: Identifier< number >,
    incomingInvoicePayment: IncomingInvoicePayment
) : InsertIncomingInvoicePaymentAction {
    return { type: INSERT_INCOMING_INVOICE_PAYMENT, incomingInvoicePayment, invoiceId: incomingInvoiceId };
}
export function insertIncomingInvoicePaymentSuccess() : InsertIncomingInvoicePaymentSuccessAction {
    return { type: INSERT_INCOMING_INVOICE_PAYMENT_SUCCESS };
}
export function insertIncomingInvoicePaymentError( error: Error ): InsertIncomingInvoicePaymentErrorAction {
    return { type: INSERT_INCOMING_INVOICE_PAYMENT_ERROR, error };
}

/**
 * redux observable
 */
export function insertIncomingInvoicePaymentEpic(
    action$: Observable< InsertIncomingInvoicePaymentAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( INSERT_INCOMING_INVOICE_PAYMENT ),
        switchMap(action => {
            const path = `${ apiPath( action.invoiceId.rawValue ) }/payments`;

            return ajax(
                APP_CONFIG.getRequestObject(
                    path,
                    RouteMethods.post,
                    action.incomingInvoicePayment
                )
            ).pipe(
                mergeMap(_ => of(
                    insertIncomingInvoicePaymentSuccess(),
                    fetchIncomingInvoiceById( action.invoiceId )
                )),
                catchError(error => of(
                    insertIncomingInvoicePaymentError( error),
                    eventNotificationError(error, store$, IncomingInvoicePaymentModule.identifier, "insertIncomingInvoicePayment")
                ))
            );

        })
    );
}

/**
 * delete invoice payment
 */
export interface DeleteIncomingInvoicePaymentAction extends IncomingInvoiceId, IncomingInvoicePaymentIdProp , ReduxAction< typeof DELETE_INCOMING_INVOICE_PAYMENT > {

}
export interface DeleteIncomingInvoicePaymentSuccessAction extends ReduxAction<typeof DELETE_INCOMING_INVOICE_PAYMENT_SUCCESS > {
}
export interface DeleteIncomingInvoicePaymentErrorAction extends ErrorAction< typeof DELETE_INCOMING_INVOICE_PAYMENT_ERROR >{}

export type DeleteIncomingInvoicePaymentActionTypes = DeleteIncomingInvoicePaymentAction
    | DeleteIncomingInvoicePaymentErrorAction
    | DeleteIncomingInvoicePaymentSuccessAction

/**
 * redux action constants
 */
export const DELETE_INCOMING_INVOICE_PAYMENT = "DELETE_INCOMING_INVOICE_PAYMENTS";
export const DELETE_INCOMING_INVOICE_PAYMENT_SUCCESS = "DELETE_INCOMING_INVOICE_PAYMENTS_SUCCESS";
export const DELETE_INCOMING_INVOICE_PAYMENT_ERROR = "DELETE_INCOMING_INVOICE_PAYMENTS_ERROR";

/**
 * redux action functions
 */
export function deleteIncomingInvoicePayment(
    incomingInvoiceId: Identifier< number >,
    incomingInvoicePaymentId: Identifier<number>
) : DeleteIncomingInvoicePaymentAction {
    return { type: DELETE_INCOMING_INVOICE_PAYMENT, incomingInvoicePaymentId, invoiceId: incomingInvoiceId };
}
export function deleteIncomingInvoicePaymentSuccess() : DeleteIncomingInvoicePaymentSuccessAction {
    return { type: DELETE_INCOMING_INVOICE_PAYMENT_SUCCESS };
}
export function deleteIncomingInvoicePaymentError( error: Error ): DeleteIncomingInvoicePaymentErrorAction {
    return { type: DELETE_INCOMING_INVOICE_PAYMENT_ERROR, error };
}

/**
 * redux observable
 */
export function deleteIncomingInvoicePaymentEpic(
    action$: Observable< DeleteIncomingInvoicePaymentAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( DELETE_INCOMING_INVOICE_PAYMENT ),
        switchMap(action => {
            const path = `${ apiPath( action.invoiceId.rawValue ) }/payments/`+ action.incomingInvoicePaymentId.rawValue;

            return ajax(
                APP_CONFIG.getRequestObject( path, RouteMethods.delete )
            ).pipe(
                mergeMap(_ => of(
                    deleteIncomingInvoicePaymentSuccess(),
                    fetchIncomingInvoiceById( action.invoiceId )
                )),
                catchError(error => of(
                    deleteIncomingInvoicePaymentError( error),
                    eventNotificationError(error, store$, IncomingInvoicePaymentModule.identifier, "deleteIncomingInvoicePayment")
                ))
            );

        })
    );
}